// Pure pack-desk state builders shared by the dispatch page (open / restore),
// the pack screen (Reset order) and the desk-session save queue.
import type {
    DeskClaim, DeskInfo, DeskPackState, DispatchBox, DispatchOrder, DispatchOrderItem,
} from "../../types/Dispatch";
import type { ResolvedUnit } from "./dispatch-scan-tote";
import { scannableBarcodes } from "./itemBarcodes";
import { applyPackState } from "./pack-state";

// The scanned dispatch unit (tote or sorted bay location) + the orders still
// to pack from it. Saved on the account (desk session `unit`).
export type ToteSession = {
    unitType: ResolvedUnit["unitType"]; tote_id?: string; label?: string;
    // A two-tote Putwall order lists both totes so the pack view can tell
    // the packer to fetch the second one (see ResolvedUnit.totes).
    totes?: { tote_id: string; items: number }[];
    orders: DispatchOrder[];
};

/** The upstream-missing units of a line (falls back to the current count). */
const upstreamMissing = (item: DispatchOrderItem): number =>
    item.upstreamMissingCount ?? (item.missingCount || 0);

export type FreshOrderState = { order: DispatchOrder; boxes: DispatchBox[]; markedSku: string | null };

/**
 * A just-opened order. scannedSku is the item scan that identified the order
 * (Single totes) and lands pre-scanned into Box 1; null means the order was
 * identified by its UNIT scan alone — every line starts pending. Units
 * pre-marked missing upstream by Picking load already resolved.
 */
export const freshOrderState = (order: DispatchOrder, scannedSku: string | null): FreshOrderState => {
    let markedSku: string | null = null;
    const items: DispatchOrderItem[] = order.items.map(item => {
        const premissing = upstreamMissing(item);
        // Match on the SKU or ANY of the line's scannable barcodes — the same
        // rule the pack screen and the item-scan screen use.
        if (scannedSku !== null && markedSku === null
            && (item.sku === scannedSku || scannableBarcodes(item).includes(scannedSku))) {
            markedSku = item.sku;
            const isComplete = 1 + premissing >= item.quantity;
            return {
                ...item,
                upstreamMissingCount: premissing,
                scanStatus: isComplete ? "scanned" as const : "pending" as const,
                scannedCount: 1,
                missingCount: premissing,
                damagedCount: 0,
                wrongItemCount: 0,
                barcodeDamagedCount: 0,
                assignedBoxes: [1],
            };
        }
        return {
            ...item,
            upstreamMissingCount: premissing,
            scanStatus: premissing >= item.quantity ? "missing" as const : "pending" as const,
            scannedCount: 0,
            missingCount: premissing,
            damagedCount: 0,
            wrongItemCount: 0,
            barcodeDamagedCount: 0,
            assignedBoxes: [],
        };
    });
    // Box contents are SKUs everywhere else, so seed with the SKU the scan
    // resolved to — never the raw barcode — and only when a line matched.
    const boxes: DispatchBox[] = [{ id: 1, items: markedSku !== null ? [markedSku] : [] }];
    return { order: { ...order, items }, boxes, markedSku };
};

/**
 * The pack desk as it is saved: the flag snapshot shape plus active_box.
 * Also what a flag sends, so the leader resolving it opens the desk as the
 * packer left it.
 */
export const buildPackState = (
    order: DispatchOrder,
    packBoxes: DispatchBox[],
    activeBox?: number,
): DeskPackState => ({
    items: order.items
        .filter(i => i.line_position !== undefined)
        .map(i => ({
            line_position: i.line_position as number,
            scannedCount: i.scannedCount || 0,
            missingCount: i.missingCount || 0,
            damagedCount: i.damagedCount || 0,
            wrongItemCount: i.wrongItemCount || 0,
            barcodeDamagedCount: i.barcodeDamagedCount || 0,
            assignedBoxes: Array.isArray(i.assignedBoxes) ? i.assignedBoxes : [],
        })),
    boxes: packBoxes.map(b => ({
        id: b.id,
        items: Array.isArray(b.items) ? b.items : [],
        boxRef: b.boxRef ?? null,
        boxName: b.boxName ?? null,
        boxLengthMm: b.boxLengthMm ?? null,
        boxWidthMm: b.boxWidthMm ?? null,
        boxHeightMm: b.boxHeightMm ?? null,
        boxWeightGrams: b.boxWeightGrams ?? null,
        // A weighed own-packaging container (only when present, so older
        // snapshots keep their shape).
        ...(b.measuredWeightGrams != null && b.measuredUnits != null
            ? { measuredWeightGrams: b.measuredWeightGrams, measuredUnits: b.measuredUnits }
            : {}),
    })),
    ...(activeBox !== undefined ? { active_box: activeBox } : {}),
});

/**
 * The flag snapshot for an order whose item never turned up at the desk
 * (Singles tote › Flag missing): every unit on every line marked missing and
 * no boxes — what the pack screen would send had the packer opened the
 * order, marked each unit missing and flagged it.
 */
export const allMissingPackState = (order: DispatchOrder): DeskPackState => ({
    items: order.items
        .filter(i => i.line_position !== undefined)
        .map(i => ({
            line_position: i.line_position as number,
            scannedCount: 0,
            missingCount: i.quantity,
            damagedCount: 0,
            wrongItemCount: 0,
            barcodeDamagedCount: 0,
            assignedBoxes: [],
        })),
    boxes: [],
});

export const highestBoxId =(boxes: DispatchBox[]): number =>
    boxes.length > 0 ? Math.max(...boxes.map(b => b.id)) : 1;

/** The saved active box when that box still exists, else the highest box id. */
export const activeBoxFor = (state: DeskPackState | null | undefined, boxes: DispatchBox[]): number =>
    state?.active_box !== undefined && boxes.some(b => b.id === state.active_box)
        ? state.active_box
        : highestBoxId(boxes);

/**
 * Re-hydrate a saved pack state onto an order. Returns null when the state is
 * empty (no items, no boxes) so the caller falls back to a fresh open.
 */
export const restoreOrderState = (
    order: DispatchOrder,
    packState: DeskPackState | null | undefined,
): { order: DispatchOrder; boxes: DispatchBox[]; activeBox: number } | null => {
    if (!packState) return null;
    const base = freshOrderState(order, null).order;
    const applied = applyPackState(base, packState);
    if (!applied) return null;
    return { order: applied.order, boxes: applied.boxes, activeBox: activeBoxFor(packState, applied.boxes) };
};

export type ResetCleared = { boxes: number; units: number; marked: number };

/**
 * Reset order (plan 7a): one empty Box 1, every line back to pending as if
 * the order were just opened. Units pre-marked missing upstream stay marked
 * — the desk did not set them. `cleared` is what the log row records.
 */
export const resetOrderState = (
    order: DispatchOrder,
    boxes: DispatchBox[],
): { order: DispatchOrder; boxes: DispatchBox[]; activeBox: number; cleared: ResetCleared } => {
    let units = 0;
    let marked = 0;
    const items: DispatchOrderItem[] = order.items.map(item => {
        const premissing = upstreamMissing(item);
        units += (item.scannedCount || 0) + (item.barcodeDamagedCount || 0);
        marked += Math.max(0, (item.missingCount || 0) - premissing)
            + (item.damagedCount || 0) + (item.wrongItemCount || 0);
        return {
            ...item,
            upstreamMissingCount: premissing,
            scanStatus: premissing >= item.quantity ? "missing" as const : "pending" as const,
            scannedCount: 0,
            missingCount: premissing,
            damagedCount: 0,
            wrongItemCount: 0,
            barcodeDamagedCount: 0,
            assignedBoxes: [],
        };
    });
    return {
        order: { ...order, items },
        boxes: [{ id: 1, items: [] }],
        activeBox: 1,
        cleared: { boxes: boxes.length, units, marked },
    };
};

/** True when the desk holds anything a Reset order would clear. */
export const hasDeskProgress = (order: DispatchOrder, boxes: DispatchBox[]): boolean =>
    order.items.some(i =>
        (i.scannedCount || 0) > 0
        || (i.barcodeDamagedCount || 0) > 0
        || (i.damagedCount || 0) > 0
        || (i.wrongItemCount || 0) > 0
        || (i.missingCount || 0) > upstreamMissing(i))
    || boxes.some(b => !!b.boxRef || !!b.boxName || (b.items?.length ?? 0) > 0 || !!b.boxLengthMm)
    || boxes.length > 1;

/** Identity of a saved desk state — lets the page skip a PUT that would change nothing. */
export const packStateKey = (packState: DeskPackState, view: string): string =>
    `${view}|${JSON.stringify(packState)}`;

/** Signature of the box list's assignments — a change here is a box scan and saves at once. */
export const boxSignature = (boxes: DispatchBox[]): string =>
    boxes.map(b => `${b.id}:${b.boxRef ?? ""}:${b.boxName ?? ""}:${b.boxLengthMm ?? ""}`).join("|");

// ── Restore from the server session ────────────────────────────────

export type DeskView = "scan-desk" | "scan-tote" | "scan-item" | "order-pack";

export type RestoredDesk = {
    desk: DeskInfo | null;
    tote: ToteSession | null;
    order: { order: DispatchOrder; boxes: DispatchBox[]; activeBox: number } | null;
    view: DeskView;
};

export const buildRestoredDesk = (s: {
    warehouse: string; desk_id: string | null; unit: unknown | null;
    order: DispatchOrder | null; pack_state: DeskPackState | null;
}): RestoredDesk => {
    const desk: DeskInfo | null = s.desk_id ? { deskId: s.desk_id, warehouse: s.warehouse } : null;
    const rawUnit = s.unit as ToteSession | null;
    const tote = rawUnit && typeof rawUnit === "object" && Array.isArray(rawUnit.orders) ? rawUnit : null;
    let order: RestoredDesk["order"] = null;
    if (s.order && desk) {
        const restored = restoreOrderState(s.order, s.pack_state);
        if (restored) order = restored;
        else {
            const fresh = freshOrderState(s.order, null);
            order = { order: fresh.order, boxes: fresh.boxes, activeBox: 1 };
        }
    }
    // confirm-ship keeps no courier result, so an open order always lands on the pack screen.
    const view: DeskView = order ? "order-pack" : desk ? (tote ? "scan-item" : "scan-tote") : "scan-desk";
    return { desk, tote, order, view };
};

// ── Claim notice ───────────────────────────────────────────────────

type T = (key: string, vars?: Record<string, string>) => string;

/** HH:MM today, otherwise "dd Mon HH:MM". */
export const formatClaimSince = (iso: string | null | undefined, now: Date = new Date()): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (d.toDateString() === now.toDateString()) return time;
    return `${d.toLocaleDateString([], { day: "2-digit", month: "short" })} ${time}`;
};

/** "{name} is packing this order at {desk} since {time}. Ask a team leader to release it." */
export const claimNotice = (t: T, claim: DeskClaim | null | undefined): string => {
    const name = claim?.name || t("desk_claim_unknown_packer");
    const since = formatClaimSince(claim?.since);
    return claim?.desk_id
        ? t("desk_order_claimed", { name, desk: claim.desk_id, time: since })
        : t("desk_order_claimed_no_desk", { name, time: since });
};

/** "Being packed by {name} at {desk} since {time}" (leader surfaces). */
export const claimBeingPacked = (t: T, claim: DeskClaim): string => {
    const name = claim.name || t("desk_claim_unknown_packer");
    const since = formatClaimSince(claim.since);
    return claim.desk_id
        ? t("order_being_packed", { name, desk: claim.desk_id, time: since })
        : t("order_being_packed_no_desk", { name, time: since });
};
